import type { NavigateFunction, Location } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import type { UploadedFile, BCPNavigationState } from '../types/navigation';
import type { UploadData } from '../types/bcp-generation';
import { logger } from './logger';

export type BCPType = 'introductory' | 'simple' | 'basic';

export interface TypedLocationState extends BCPNavigationState {
  // Add other possible state properties here
}

export interface NavigationOptions {
  delay?: number;
}

class NavigationService {
  /**
   * Navigate to BCP creation page with required state
   */
  navigateToBCPCreate(
    data: BCPNavigationState & NavigationOptions,
    navigate: NavigateFunction
  ): void {
    logger.info('Navigating to BCP create page', {
      component: 'NavigationService',
      action: 'navigateToBCPCreate',
      data: {
        bcpType: data.bcpType,
        filesCount: data.uploadedFiles?.length || 0,
        hasCompanyInfo: !!data.companyInfo
      }
    });

    const { delay, ...state } = data;

    const doNavigate = () => {
      // BCPタイプに基づいて適切なルートを選択
      let route: string = ROUTES.CREATE_BCP; // デフォルトは簡易版
      if (state.bcpType === 'introductory') {
        route = ROUTES.CREATE_BCP_INTRO;
      } else if (state.bcpType === 'basic') {
        // 基本版は復旧方針書ページへ直接遷移(新規作成時はbcpIdなし)
        route = ROUTES.RESTORE_POLICY_SIMPLE;
      }

      const navigationState: TypedLocationState = {
        uploadedFiles: state.uploadedFiles,
        apiResponse: state.apiResponse,
        allUploadResults: state.allUploadResults,
        bcpType: state.bcpType,
        companyInfo: state.companyInfo
      };

      logger.debug('Executing navigation', {
        component: 'NavigationService',
        action: 'doNavigate',
        data: { route, state: navigationState }
      });

      navigate(route, { state: navigationState });
    };

    if (delay && delay > 0) {
      setTimeout(doNavigate, delay);
    } else {
      doNavigate();
    }
  }

  /**
   * Navigate to upload page
   */
  navigateToUpload(navigate: NavigateFunction): void {
    navigate(ROUTES.UPLOAD);
  }

  /**
   * Navigate to home page
   */
  navigateToHome(navigate: NavigateFunction): void {
    navigate(ROUTES.HOME);
  }

  /**
   * Check if uploaded files exist, redirect to upload if not
   * @returns true if files exist, false if redirected
   */
  requireUploadedFiles(
    uploadedFiles: UploadedFile[] | null | undefined,
    navigate: NavigateFunction
  ): boolean {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      this.navigateToUpload(navigate);
      return false;
    }
    return true;
  }

  /**
   * Navigate to any route with state
   */
  navigateWithState<T = any>(
    path: string,
    state: T | undefined,
    navigate: NavigateFunction
  ): void {
    navigate(path, state ? { state } : {});
  }

  /**
   * Navigate back in history
   */
  goBack(navigate: NavigateFunction): void {
    navigate(-1);
  }

  /**
   * Navigate to company info page with extracted data
   */
  navigateToCompanyInfo(
    data: {
      companyInfo: NonNullable<BCPNavigationState['companyInfo']>;
      uploadedFiles?: UploadedFile[];
      bcpType?: BCPType;
      apiResponse?: UploadData;
      allUploadResults?: any[];
    },
    navigate: NavigateFunction
  ): void {
    logger.info('Navigating to company info page', {
      component: 'NavigationService',
      action: 'navigateToCompanyInfo',
    });

    const navigationState: BCPNavigationState = {
      uploadedFiles: data.uploadedFiles || [],
      bcpType: data.bcpType,
      companyInfo: data.companyInfo,
      apiResponse: data.apiResponse,
      allUploadResults: data.allUploadResults,
    };

    navigate(ROUTES.COMPANY_INFO, { state: navigationState });
  }

  /**
   * Extract state from location object with type safety
   */
  extractLocationState<T = TypedLocationState>(
    location: Location,
    defaults?: Partial<T>
  ): T {
    const state = location.state || {};
    const result = defaults ? { ...defaults, ...state } : state;
    
    logger.debug('Extracting location state', {
      component: 'NavigationService',
      action: 'extractLocationState',
      data: { state: result }
    });
    
    return result as T;
  }

  /**
   * Extract BCP navigation state with proper typing and validation
   */
  extractBCPNavigationState(location: Location): BCPNavigationState {
    const state = this.extractLocationState<BCPNavigationState>(location, {
      uploadedFiles: [],
      bcpType: 'simple'
    });

    // Validate required fields
    if (!Array.isArray(state.uploadedFiles)) {
      logger.warn('Invalid uploadedFiles in location state', {
        component: 'NavigationService',
        action: 'extractBCPNavigationState',
        data: { uploadedFiles: state.uploadedFiles }
      });
      state.uploadedFiles = [];
    }

    return state;
  }

  /**
   * Create a hook that provides navigation helpers
   */
  createNavigationHook() {
    return function useAppNavigation() {
      const navigate = useNavigate();

      return {
        navigateToBCPCreate: (data: BCPNavigationState & NavigationOptions) =>
          navigationService.navigateToBCPCreate(data, navigate),
        navigateToUpload: () =>
          navigationService.navigateToUpload(navigate),
        navigateToHome: () =>
          navigationService.navigateToHome(navigate),
        requireUploadedFiles: (files: UploadedFile[] | null | undefined) =>
          navigationService.requireUploadedFiles(files, navigate),
        navigateWithState: <T = any>(path: string, state?: T) =>
          navigationService.navigateWithState(path, state, navigate),
        goBack: () =>
          navigationService.goBack(navigate),
        navigateToCompanyInfo: (data: {
          companyInfo: NonNullable<BCPNavigationState['companyInfo']>;
          uploadedFiles?: UploadedFile[];
          bcpType?: BCPType;
          apiResponse?: UploadData;
          allUploadResults?: any[];
        }) =>
          navigationService.navigateToCompanyInfo(data, navigate),
      };
    };
  }
}

// Export singleton instance
export const navigationService = new NavigationService();

// Export convenience hook
export const useAppNavigation = navigationService.createNavigationHook();